import { SelectValidate, ShowHide, TextFieldValidate } from '@/components';
import BoxInfo from '@/components/boxInfo';
import { BoxInfoProps } from '@/components/boxInfo/boxInfo';
import { BodyForm } from '@/components/formValidate';
import { maxLength } from '@/components/formValidate/validates/maxLength';
import GridGroup, { GridGroupContentProps } from '@/components/gridGroup';
import { Address } from '@/shared/modules/address/entities/address';
import { MoveSheetContent, MoveSheetError, MoveSheetNormalized } from '@/modules/move/entities';
import {
  MOVE_SHEET_COLUMN,
  MOVE_SHEET_COLUMN_LABEL,
} from '@/modules/move/entities/constants/moveSheetColumn';
import {
  BANK_ACCOUNT_TYPE_TRANSLATE,
  MOVE_SHEET_BANK_ACCOUNT_TYPE,
  MOVE_SHEET_DEGREE_KINSHIP,
  MOVE_SHEET_GENDER,
  MOVE_SHEET_MARITAL_STATUS,
  MOVE_SHEET_OWNERSHIP,
} from '@/modules/move/entities/constants/moveSheetValues';
import TooltipDescription from '@/pages/beneficiaries/createOrUpdate/components/tooltipDescription';
import {
  BENEFICIARY_OWNERSHIP,
  KINSHIP_DEGREE,
  MOVE_FILE_COLUMN_DESCRIPTION,
  MOVE_FILE_COLUMN_NAME,
} from '@/pages/moves/shared/constants';
import { getStateOptions } from '@/shared/modules/address';
import { useAddressFinder } from '@/shared/modules/address/hooks';
import ICONS from '@/shared/entities/constants/icons';
import { isEmpty, isNumber } from 'lodash';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Checkbox, Text } from 'vkit/lib/components';
import { Grid } from 'vkit/lib/context';

export interface FormFieldsProps extends BodyForm {
  onlyFieldWithError?: boolean;
  rowToEdition: MoveSheetContent | null;
  setRowError: Dispatch<SetStateAction<MoveSheetError>>;
  rowError: MoveSheetError;
  setFormData: Dispatch<SetStateAction<MoveSheetContent | null>>;
  originalRow: MoveSheetNormalized | null;
  hasBankAccount: boolean;
  setHasBankAccount: Dispatch<SetStateAction<boolean>>;
  isLifeActive: boolean;
}

export const FormFields: React.FC<FormFieldsProps> = ({
  useErrors,
  useValues,
  onChange,
  onBlur,
  onlyFieldWithError,
  setRowError,
  rowError,
  rowToEdition,
  setFormData,
  originalRow,
  hasBankAccount,
  setHasBankAccount,
  isLifeActive,
}) => {
  const initialStateDisabledFields = {
    [MOVE_SHEET_COLUMN.cidade]: true,
    [MOVE_SHEET_COLUMN.bairro]: true,
    [MOVE_SHEET_COLUMN.logradouro]: true,
    [MOVE_SHEET_COLUMN.uf]: true,
    [MOVE_SHEET_COLUMN.numero]: true,
    [MOVE_SHEET_COLUMN.complemento]: true,
  };
  const [disabled, setDisabled] = useState<Partial<Record<MOVE_SHEET_COLUMN, boolean>>>(
    initialStateDisabledFields,
  );

  const isDependent = rowToEdition?.titularidade === MOVE_SHEET_OWNERSHIP.DEPENDENT;

  const canDisableField = (key: MOVE_SHEET_COLUMN) => {
    const isValueEmpty =
      !isNumber(originalRow?.originalContent?.[key]) &&
      isEmpty(originalRow?.originalContent?.[key]);
    return (onlyFieldWithError && !rowError[key] && !isValueEmpty) || isLifeActive;
  };

  const canDisableAddressFields = () => {
    return [
      MOVE_SHEET_COLUMN.cep,
      MOVE_SHEET_COLUMN.logradouro,
      MOVE_SHEET_COLUMN.bairro,
      MOVE_SHEET_COLUMN.cidade,
      MOVE_SHEET_COLUMN.uf,
    ].every((field) => canDisableField(field));
  };

  const canDisableBankFields = () => {
    return [
      MOVE_SHEET_COLUMN.creditoBanco,
      MOVE_SHEET_COLUMN.creditoNumAgencia,
      MOVE_SHEET_COLUMN.creditoDVAgencia,
      MOVE_SHEET_COLUMN.creditoNumConta,
      MOVE_SHEET_COLUMN.creditoDVConta,
    ].every((field) => canDisableField(field) || !hasBankAccount);
  };

  const validateAdressFields = (adress: Address) => {
    const messageErrorByField = {
      [MOVE_SHEET_COLUMN.cidade]: maxLength(25)(adress.city),
      [MOVE_SHEET_COLUMN.bairro]: maxLength(25)(adress.neighborhood),
      [MOVE_SHEET_COLUMN.logradouro]: maxLength(35)(adress.location),
    };

    setRowError((current) => {
      return {
        ...current,
        ...messageErrorByField,
      };
    });

    Object.entries(messageErrorByField).forEach(([field, message]) => {
      if (message) {
        setDisabled((current) => ({
          ...current,
          [field]: false,
        }));
      }
    });
  };

  const [disabledNumber, setDisabledNumber] = useState(false);
  const { loading: loadingAddress, searchAddress } = useAddressFinder({
    searchAddressCallback: (foundAddress, zipcode) => {
      setFormData((current) => {
        return {
          ...current,
          [MOVE_SHEET_COLUMN.cidade]: foundAddress.city,
          [MOVE_SHEET_COLUMN.bairro]: foundAddress.neighborhood,
          [MOVE_SHEET_COLUMN.logradouro]: foundAddress.location,
          [MOVE_SHEET_COLUMN.uf]: foundAddress.state_abbr,
        };
      });

      const canDisable = zipcode.length !== 8;

      setDisabled({
        [MOVE_SHEET_COLUMN.cidade]: canDisable || Boolean(foundAddress.city),
        [MOVE_SHEET_COLUMN.bairro]: canDisable || Boolean(foundAddress.neighborhood),
        [MOVE_SHEET_COLUMN.logradouro]: canDisable || Boolean(foundAddress.location),
        [MOVE_SHEET_COLUMN.uf]: canDisable || Boolean(foundAddress.state_abbr),
        [MOVE_SHEET_COLUMN.numero]: canDisable,
        [MOVE_SHEET_COLUMN.complemento]: canDisable,
      });

      if (!isEmpty(foundAddress)) {
        validateAdressFields(foundAddress);
      }
    },
  });

  const clearBankAccountFields = () => {
    setFormData((current) => {
      return {
        ...current,
        [MOVE_SHEET_COLUMN.tipoContaBancaria]: '',
        [MOVE_SHEET_COLUMN.creditoDVConta]: '',
        [MOVE_SHEET_COLUMN.creditoNumConta]: '',
        [MOVE_SHEET_COLUMN.creditoNumAgencia]: '',
        [MOVE_SHEET_COLUMN.creditoDVAgencia]: '',
        [MOVE_SHEET_COLUMN.creditoBanco]: '',
      };
    });
  };

  useEffect(() => {
    const areAllBankAccountFieldsEmpty = [
      MOVE_SHEET_COLUMN.creditoBanco,
      MOVE_SHEET_COLUMN.creditoNumAgencia,
      MOVE_SHEET_COLUMN.creditoDVAgencia,
      MOVE_SHEET_COLUMN.creditoNumConta,
      MOVE_SHEET_COLUMN.creditoDVConta,
    ].every(
      (fieldKey) =>
        isEmpty(originalRow?.originalContent?.[fieldKey]) && isEmpty(rowToEdition?.[fieldKey]),
    );

    setHasBankAccount(!areAllBankAccountFieldsEmpty);
  }, [originalRow?.originalContent, rowToEdition, setHasBankAccount]);

  const boxInfos: (Partial<BoxInfoProps> & { body: GridGroupContentProps[] })[] = [
    {
      title: 'Contrato',
      iconImageUrl: ICONS.CONTRACT,
      body: [
        {
          default: 25,
          middle: 100,
          component: (
            <TextFieldValidate
              maxLength={260}
              required
              name={MOVE_SHEET_COLUMN.plano}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.plano]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.plano)}
            />
          ),
        },
        {
          default: 25,
          middle: 100,
          component: (
            <TextFieldValidate
              maxLength={260}
              required
              name={MOVE_SHEET_COLUMN.contrato}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.contrato]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.contrato)}
            />
          ),
        },
        {
          default: 25,
          middle: 100,
          component: (
            <TextFieldValidate
              maxLength={260}
              required
              name={MOVE_SHEET_COLUMN.subfatura}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.subfatura]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.subfatura)}
            />
          ),
        },
        {
          default: 25,
          middle: 100,
          component: (
            <TextFieldValidate
              maxLength={260}
              required
              name={MOVE_SHEET_COLUMN.matricula}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.matricula]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.matricula)}
            />
          ),
        },
      ],
    },
    {
      title: 'Informações do beneficiário',
      icon: ICONS.PERSON,
      body: [
        {
          default: 100,
          middle: 100,
          component: (
            <TextFieldValidate
              maxLength={260}
              required
              name={MOVE_SHEET_COLUMN.nome}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.nome]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.nome)}
            />
          ),
        },
        {
          default: 33.33,
          middle: 100,
          component: (
            <SelectValidate
              required={
                useValues[MOVE_SHEET_COLUMN.titularidade] === BENEFICIARY_OWNERSHIP.DEPENDENT
              }
              label='Grau de parentesco com o titular'
              name={MOVE_SHEET_COLUMN.grauParentesco}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.grauParentesco)}
              data={[
                {
                  text: 'Cônjuge',
                  value: MOVE_SHEET_DEGREE_KINSHIP.SPOUSE,
                },
                {
                  text: 'Filho',
                  value: MOVE_SHEET_DEGREE_KINSHIP.CHILD,
                },
                {
                  text: 'Mãe',
                  value: MOVE_SHEET_DEGREE_KINSHIP.MOTHER,
                },
                {
                  text: 'Pai',
                  value: MOVE_SHEET_DEGREE_KINSHIP.FATHER,
                },
                {
                  text: 'Sogro',
                  value: MOVE_SHEET_DEGREE_KINSHIP.FATHER_IN_LAW,
                },
                {
                  text: 'Sogra',
                  value: MOVE_SHEET_DEGREE_KINSHIP.MOTHER_IN_LAW,
                },
                {
                  text: 'Tutelado',
                  value: MOVE_SHEET_DEGREE_KINSHIP.GUARDIAN,
                },
                {
                  text: 'Outros',
                  value: MOVE_SHEET_DEGREE_KINSHIP.OTHERS,
                },
              ]}
            />
          ),
        },
        {
          default: 33.33,
          component: (
            <TextFieldValidate
              required
              type='date'
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.dataNascimento]}
              name={MOVE_SHEET_COLUMN.dataNascimento}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.dataNascimento)}
            />
          ),
        },
        {
          default: 33.33,
          component: (
            <SelectValidate
              required
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.sexo]}
              name={MOVE_SHEET_COLUMN.sexo}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.sexo)}
              data={[
                {
                  text: 'Masculino',
                  value: MOVE_SHEET_GENDER.MALE,
                },
                {
                  text: 'Feminino',
                  value: MOVE_SHEET_GENDER.FEMALE,
                },
              ]}
            />
          ),
        },
        {
          default: 33.33,
          component: (
            <SelectValidate
              required
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.estadoCivil]}
              name={MOVE_SHEET_COLUMN.estadoCivil}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.estadoCivil)}
              data={[
                {
                  text: 'Solteiro',
                  value: MOVE_SHEET_MARITAL_STATUS.SINGLE,
                },
                {
                  text: 'Casado',
                  value: MOVE_SHEET_MARITAL_STATUS.MARRIED,
                },
                {
                  text: 'Viúva(o)',
                  value: MOVE_SHEET_MARITAL_STATUS.WIDOWED,
                },
                {
                  text: 'Outros',
                  value: MOVE_SHEET_MARITAL_STATUS.OTHERS,
                },
              ]}
            />
          ),
        },
        {
          default: 33.33,
          component: (
            <Grid column gap={8}>
              <TextFieldValidate
                required={!useValues.skipValidationCPF}
                type='cpf'
                label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.cpf]}
                name={MOVE_SHEET_COLUMN.cpf}
                useValues={useValues}
                useErrors={useErrors}
                onChange={onChange}
                onBlur={onBlur}
                mask='999.999.999-99'
                disabled={
                  (useValues.skipValidationCPF && isDependent) ||
                  canDisableField(MOVE_SHEET_COLUMN.cpf)
                }
              />

              <ShowHide visible={isDependent} transition='slideToDown'>
                <Checkbox
                  small
                  label='Dependente ainda não possui CPF'
                  checked={useValues.skipValidationCPF}
                  onChange={(checked: boolean) => {
                    onChange({
                      skipValidationCPF: checked,
                      [MOVE_SHEET_COLUMN.cpf]: '',
                    });
                  }}
                />
              </ShowHide>
            </Grid>
          ),
        },
        {
          default: 33.33,
          component: (
            <TextFieldValidate
              required={isDependent}
              type='cpf'
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.cpfTitular]}
              name={MOVE_SHEET_COLUMN.cpfTitular}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              mask='999.999.999-99'
              disabled={!isDependent || canDisableField(MOVE_SHEET_COLUMN.cpfTitular)}
            />
          ),
        },
        {
          default: 33.33,
          middle: 50,
          component: (
            <TextFieldValidate
              required={useValues[MOVE_SHEET_COLUMN.grauParentesco] === KINSHIP_DEGREE.SPOUSE}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.codElegibilidade]}
              name={MOVE_SHEET_COLUMN.codElegibilidade}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.codElegibilidade)}
            />
          ),
        },

        {
          default: 33.33,
          middle: 100,
          component: (
            <TextFieldValidate
              header={
                <TooltipDescription
                  description={
                    MOVE_FILE_COLUMN_DESCRIPTION[MOVE_FILE_COLUMN_NAME.CODIGO_DO_ANGARIADOR]
                  }
                />
              }
              required
              type='number'
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.codAngariador]}
              name={MOVE_SHEET_COLUMN.codAngariador}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.codAngariador)}
            />
          ),
        },
        {
          default: 33.33,
          middle: 100,
          component: (
            <TextFieldValidate
              required
              type='date'
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.dataAngariacao]}
              name={MOVE_SHEET_COLUMN.dataAngariacao}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.dataAngariacao)}
            />
          ),
        },
        {
          default: 100,
          middle: 100,
          component: (
            <TextFieldValidate
              required={!isDependent}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.emailSegurado]}
              name={MOVE_SHEET_COLUMN.emailSegurado}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.emailSegurado)}
            />
          ),
        },
        {
          default: 100,
          middle: 100,
          component: (
            <TextFieldValidate
              maxLength={260}
              required
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.nomeMae]}
              name={MOVE_SHEET_COLUMN.nomeMae}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.nomeMae)}
            />
          ),
        },
        {
          default: 33.33,
          middle: 50,
          component: (
            <TextFieldValidate
              header={
                <TooltipDescription
                  description={MOVE_FILE_COLUMN_DESCRIPTION[MOVE_FILE_COLUMN_NAME.CNS]}
                />
              }
              maxLength={32}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.cns]}
              name={MOVE_SHEET_COLUMN.cns}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.cns)}
            />
          ),
        },
        {
          default: 33.33,
          middle: 50,
          component: (
            <TextFieldValidate
              type='date'
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.dataCasamento]}
              name={MOVE_SHEET_COLUMN.dataCasamento}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.dataCasamento)}
            />
          ),
        },
        {
          default: 33.33,
          middle: 50,
          component: (
            <TextFieldValidate
              required
              type='date'
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.dataInicioVigencia]}
              name={MOVE_SHEET_COLUMN.dataInicioVigencia}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              disabled={canDisableField(MOVE_SHEET_COLUMN.dataInicioVigencia)}
            />
          ),
        },
      ],
    },
    {
      title: 'Endereço',
      icon: ICONS.ADDRESS,
      body: [
        {
          default: 25,
          component: (
            <TextFieldValidate
              name={MOVE_SHEET_COLUMN.cep}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.cep]}
              onBlur={onBlur}
              onChange={(name: string, value: string) => {
                onChange(name, value);
                searchAddress(value);
              }}
              required={!isDependent || hasBankAccount}
              useErrors={useErrors}
              useValues={useValues}
              mask='99999-999'
              loading={loadingAddress}
              disabled={canDisableAddressFields()}
            />
          ),
        },
        {
          default: 50,
          component: (
            <TextFieldValidate
              required={!isDependent}
              name={MOVE_SHEET_COLUMN.logradouro}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.logradouro]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              loading={loadingAddress}
              disabled={disabled[MOVE_SHEET_COLUMN.logradouro]}
              header={
                <Text value={`${(useValues[MOVE_SHEET_COLUMN.logradouro] ?? '').length}/35`} />
              }
            />
          ),
        },
        {
          default: 25,
          component: (
            <Grid column gap={8}>
              <TextFieldValidate
                name={MOVE_SHEET_COLUMN.numero}
                label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.numero]}
                useValues={useValues}
                useErrors={useErrors}
                onChange={onChange}
                onBlur={onBlur}
                loading={loadingAddress}
                disabled={disabledNumber || disabled[MOVE_SHEET_COLUMN.numero]}
                maxLength={6}
              />
              <Checkbox
                small
                label='Endereço sem número'
                checked={disabledNumber}
                onChange={(checked: boolean) => {
                  setDisabledNumber(checked);
                  onChange(MOVE_SHEET_COLUMN.numero, '');
                }}
              />
            </Grid>
          ),
        },
        {
          default: 40,
          component: (
            <TextFieldValidate
              required={!isDependent}
              name={MOVE_SHEET_COLUMN.bairro}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.bairro]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              loading={loadingAddress}
              disabled={disabled[MOVE_SHEET_COLUMN.bairro]}
              header={<Text value={`${(useValues[MOVE_SHEET_COLUMN.bairro] || '').length}/25`} />}
            />
          ),
        },
        {
          default: 40,
          component: (
            <TextFieldValidate
              required={!isDependent}
              name={MOVE_SHEET_COLUMN.cidade}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.cidade]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              loading={loadingAddress}
              disabled={disabled[MOVE_SHEET_COLUMN.cidade]}
              header={<Text value={`${(useValues[MOVE_SHEET_COLUMN.cidade] ?? '').length}/25`} />}
            />
          ),
        },
        {
          default: 20,
          component: (
            <SelectValidate
              required={!isDependent}
              searchable
              name={MOVE_SHEET_COLUMN.uf}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.uf]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              data={getStateOptions()}
              loading={loadingAddress}
              disabled={disabled[MOVE_SHEET_COLUMN.uf]}
              key={useValues[MOVE_SHEET_COLUMN.uf]}
            />
          ),
        },
        {
          default: 100,
          middle: 100,
          component: (
            <TextFieldValidate
              name={MOVE_SHEET_COLUMN.complemento}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.complemento]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              loading={loadingAddress}
              disabled={disabled[MOVE_SHEET_COLUMN.complemento]}
              maxLength={7}
            />
          ),
        },
      ],
    },
    {
      title: 'Dados bancários',
      icon: ICONS.NEGOTIATION,
      header: (
        <Checkbox
          small
          disabled={isDependent}
          label='Vida sem conta bancária'
          checked={!hasBankAccount}
          onChange={(isChecked: boolean) => {
            setHasBankAccount(!isChecked);
            if (isChecked) {
              clearBankAccountFields();
            } else {
              setDisabled(initialStateDisabledFields);
            }
          }}
          textLeft
        />
      ),
      body: [
        {
          default: 50,
          component: (
            <SelectValidate
              clearable
              required={hasBankAccount}
              disabled={isDependent || canDisableBankFields()}
              name={MOVE_SHEET_COLUMN.tipoContaBancaria}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.tipoContaBancaria]}
              useValues={useValues}
              useErrors={useErrors}
              onChange={onChange}
              onBlur={onBlur}
              key={useValues[MOVE_SHEET_COLUMN.tipoContaBancaria]}
              data={[
                {
                  text: BANK_ACCOUNT_TYPE_TRANSLATE[
                    MOVE_SHEET_BANK_ACCOUNT_TYPE.INDIVIDUAL_CHECKING_ACCOUNT
                  ],
                  value: MOVE_SHEET_BANK_ACCOUNT_TYPE.INDIVIDUAL_CHECKING_ACCOUNT,
                },
                {
                  text: BANK_ACCOUNT_TYPE_TRANSLATE[
                    MOVE_SHEET_BANK_ACCOUNT_TYPE.JOINT_CHECKING_ACCOUNT
                  ],
                  value: MOVE_SHEET_BANK_ACCOUNT_TYPE.JOINT_CHECKING_ACCOUNT,
                },
                {
                  text: BANK_ACCOUNT_TYPE_TRANSLATE[
                    MOVE_SHEET_BANK_ACCOUNT_TYPE.INDIVIDUAL_SAVINGS_ACCOUNT
                  ],
                  value: MOVE_SHEET_BANK_ACCOUNT_TYPE.INDIVIDUAL_SAVINGS_ACCOUNT,
                },
                {
                  text: BANK_ACCOUNT_TYPE_TRANSLATE[
                    MOVE_SHEET_BANK_ACCOUNT_TYPE.JOINT_SAVINGS_ACCOUNT
                  ],
                  value: MOVE_SHEET_BANK_ACCOUNT_TYPE.JOINT_SAVINGS_ACCOUNT,
                },
              ]}
            />
          ),
        },
        {
          default: 50,
          component: (
            <TextFieldValidate
              required={hasBankAccount}
              disabled={isDependent || canDisableBankFields()}
              header={<TooltipDescription description='Número do banco' />}
              maxLength={10}
              name={MOVE_SHEET_COLUMN.creditoBanco}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.creditoBanco]}
              onBlur={onBlur}
              onChange={onChange}
              useErrors={useErrors}
              useValues={useValues}
            />
          ),
        },
        {
          default: 50,
          component: (
            <TextFieldValidate
              required={hasBankAccount}
              disabled={isDependent || canDisableBankFields()}
              maxLength={16}
              name={MOVE_SHEET_COLUMN.creditoNumAgencia}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.creditoNumAgencia]}
              onBlur={onBlur}
              onChange={onChange}
              useErrors={useErrors}
              useValues={useValues}
            />
          ),
        },
        {
          default: 50,
          component: (
            <TextFieldValidate
              required={hasBankAccount}
              disabled={isDependent || canDisableBankFields()}
              maxLength={1}
              name={MOVE_SHEET_COLUMN.creditoDVAgencia}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.creditoDVAgencia]}
              onBlur={onBlur}
              onChange={onChange}
              useErrors={useErrors}
              useValues={useValues}
            />
          ),
        },
        {
          default: 50,
          component: (
            <TextFieldValidate
              required={hasBankAccount}
              disabled={isDependent || canDisableBankFields()}
              maxLength={16}
              name={MOVE_SHEET_COLUMN.creditoNumConta}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.creditoNumConta]}
              onBlur={onBlur}
              onChange={onChange}
              useErrors={useErrors}
              useValues={useValues}
            />
          ),
        },
        {
          default: 50,
          component: (
            <TextFieldValidate
              required={hasBankAccount}
              disabled={isDependent || canDisableBankFields()}
              maxLength={1}
              name={MOVE_SHEET_COLUMN.creditoDVConta}
              label={MOVE_SHEET_COLUMN_LABEL[MOVE_SHEET_COLUMN.creditoDVConta]}
              onBlur={onBlur}
              onChange={onChange}
              useErrors={useErrors}
              useValues={useValues}
            />
          ),
        },
      ],
    },
  ];

  return (
    <>
      {boxInfos.map(
        (boxInfo) => (
          <BoxInfo
            key={boxInfo.title}
            title={boxInfo.title}
            header={boxInfo.header}
            icon={boxInfo.icon}
            iconImageUrl={boxInfo.iconImageUrl}>
            <GridGroup body={boxInfo.body} />
          </BoxInfo>
        ),
        [],
      )}
    </>
  );
};
